<script lang="ts" setup>
import { GpBtn, GpBtnLink, GpTable, GpTableColumn, type GpTableDataRow } from '@gportal/uikit';
import ProjectSettingsLayout from '@panel/layouts/Layout.Project.Settings.vue';
import {
  type ProjectCloudProvider,
  ProjectMemberRole,
} from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import {
  type ProjectCloudProvidersRequest,
  useProjectStore,
} from 'gpcore-apikit/store/pinia/project';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import useGpTableData from '@panel/composables/useGpTableData';
import type { ListProjectCloudProvidersResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import { computed, onMounted, reactive, ref } from 'vue';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import EnumTag from 'gpcore-uikit/components/Component.EnumTag.vue';
import Status from 'gpcore-uikit/components/Component.Status.vue';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import CreateCloudProvidersModal from '@panel/components/project/cloud_providers/CreateCloudProvidersModal.vue';
import EditCloudProviderModal from '@panel/components/project/cloud_providers/EditCloudProviderModal.vue';
import useViewTitle from '@panel/composables/useViewTitle';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

interface CloudProvidersTableRow extends GpTableDataRow {
  key: string;
  name: string;
  type: CloudProviderType;
  region: string;
  valid: boolean;
}

useViewTitle('Cloud providers');

const { addNotification } = useNotifications();

const projectStore = useProjectStore();

const isLoading = ref(false);
const pagination = reactive({ currentPage: 1, totalPages: 1 });

const membership = computed(() => projectStore.currentUserMembership);
const cloudProviders = computed(() => projectStore.cloudProviders);

const { selectedRows: toDelete, tableData } = useGpTableData(cloudProviders, (data) =>
  data.map((cloudProvider) => ({
    key: cloudProvider.id,
    name: cloudProvider.name,
    type: cloudProvider.type,
    region: cloudProvider.region,
    valid: cloudProvider.valid,
  })),
);

// Load
const reload = (page: number | Event) => {
  pagination.currentPage = page as number;
  const req: ProjectCloudProvidersRequest = {
    page: (page as number) + 1,
  };
  isLoading.value = true;
  projectStore
    .reloadProjectCloudProviders(req)
    .then((response: ListProjectCloudProvidersResponse) => {
      pagination.totalPages = response.pagination?.total;
    })
    .catch((error: ServiceError) => {
      addNotification({
        type: 'danger',
        message: error.message,
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

// Create
const createModal = ref(false);
const openCreateModal = () => {
  createModal.value = true;
};
const onCreated = (_: ProjectCloudProvider) => {
  createModal.value = false;
};

// Edit
const cloudProviderToEdit = ref<ProjectCloudProvider>();
const editModal = ref(false);
const openEditModal = (cloudProvider: CloudProvidersTableRow) => {
  cloudProviderToEdit.value = cloudProviders.value.find((cp) => cp.id === cloudProvider.key);
  editModal.value = true;
};
const onEdited = (_: ProjectCloudProvider) => {
  editModal.value = false;
};

// Destroy
const destroyModal = ref(false);
const isDeleting = ref(false);
const onDelete = async () => {
  if (!destroyModal.value) {
    return;
  }

  const promises = toDelete.value.map((row) => projectStore.deleteProjectCloudProvider(row.key));

  try {
    await Promise.all(promises);
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isDeleting.value = false;
    reload(0);
  }

  destroyModal.value = false;
};

// Init
onMounted(() => {
  reload(0);
});
</script>

<template>
  <ProjectSettingsLayout>
    <template #buttons>
      <template v-if="membership && membership.role === ProjectMemberRole.OWNER">
        <GpBtn
          btn-type="danger"
          outline
          size="sm"
          :disabled="toDelete.length === 0"
          :icon="['fas', 'fa-trash']"
          label="Remove"
          @click="destroyModal = true"
        />
        <GpBtn
          btn-type="primary"
          size="sm"
          :icon="['fas', 'plus']"
          label="Add"
          @click="openCreateModal()"
        />
      </template>
    </template>

    <template #content>
      <p class="tw-mb-4">
        In addition to GPCORE resources, it is possible to integrate other external cloud providers.
        GPCORE will take over the management and provisioning of the external hardware. The
        resources imported from a external cloud provider are handled in the same way as the GPCORE
        bare metal cloud servers and are managed as nodes. Deleting a node linked to an external
        cloud provider will not delete the resource on the external cloud provider.
      </p>

      <CreateCloudProvidersModal
        :show="createModal"
        @confirm="onCreated"
        @close="createModal = false"
      />
      <EditCloudProviderModal
        :show="editModal"
        :cloud-provider="cloudProviderToEdit"
        @confirm="onEdited"
        @close="editModal = false"
      />

      <ConfirmModal
        :show="destroyModal"
        confirm-text="Remove"
        confirm-type="danger"
        :loading="isDeleting"
        @close="destroyModal = false"
        @confirm="onDelete"
      />

      <GpTable
        :data="tableData"
        pagination
        :page="pagination.currentPage"
        :max-pages="pagination.totalPages"
        :is-loading="isLoading"
        empty-message="No cloud providers found."
        loading-message="Loading cloud providers ..."
        @update:page="reload"
        v-model:selected-rows="toDelete"
      >
        <GpTableColumn
          v-if="membership && membership.role === ProjectMemberRole.OWNER"
          field="checkbox"
        />
        <GpTableColumn label="State" field="valid">
          <template #body="{ row }: { row: CloudProvidersTableRow }">
            <Status v-if="row.valid" state="success" label="Active" />
            <Status v-else state="danger" label="Invalid" />
          </template>
        </GpTableColumn>
        <GpTableColumn label="Name" field="name" />
        <GpTableColumn label="Type" field="type">
          <template #body="{ row }: { row: CloudProvidersTableRow }">
            <EnumTag
              :enum-map="CloudProviderType"
              :enum-value="row.type"
              prefix="CLOUD_PROVIDER_TYPE_"
            />
          </template>
        </GpTableColumn>
        <GpTableColumn label="Region" field="region" />
        <GpTableColumn field="actions" column-class="tw-text-end">
          <template #body="{ row }: { row: CloudProvidersTableRow }">
            <router-link
              :to="{
                name: 'ProjectCloudProviderImport',
                params: {
                  projectId: projectStore.projectId,
                  providerId: row.key,
                },
              }"
              v-slot="{ href, navigate }"
              custom
            >
              <GpBtnLink
                size="sm"
                label="Import"
                class="tw-mx-2"
                :icon="['fas', 'fa-cloud-arrow-up']"
                :href="href"
                @click="navigate"
              />
            </router-link>
            <GpBtn
              v-if="membership && membership.role === ProjectMemberRole.OWNER"
              size="sm"
              btn-type="warning"
              label="Edit"
              :icon="['fas', 'fa-pencil-alt']"
              @click="openEditModal(row)"
            />
          </template>
        </GpTableColumn>
      </GpTable>
    </template>
  </ProjectSettingsLayout>
</template>
